import { DateUtils } from '@sankhyalabs/core';
import StringUtils from '../../../utils/StringUtils';
import { i18nFn } from '../../../types';
import {
  EConsumerStatus,
  EPersonType,
  IAddressInfo,
  IClientInfoData,
  ICompanyData,
  IConsumerStatusInfo,
  IContactInfo,
  IPersonalData,
} from '../subcomponents/score-dashboard/subcomponents/score-info/types';
import { IGetLatestScoreByDocumentResponse } from '../types/api';

const NOT_INFORMED_KEY = 'FinBankCreditScoreInfo.scoreInfo.values.notInformed';

export function mapToClientInfoData(response: IGetLatestScoreByDocumentResponse, i18n: i18nFn): IClientInfoData {
  const personType = derivePersonType(response.score.documentType);

  return {
    personType,
    personalData: personType === EPersonType.INDIVIDUAL ? buildPersonalData(response, i18n) : undefined,
    companyData: personType === EPersonType.COMPANY ? buildCompanyData(response, i18n) : undefined,
    contact: buildContact(response, i18n),
    addresses: buildAddresses(response),
    consumerStatus: buildConsumerStatus(response, i18n),
  };
}

function parseDateLocal(isoString: string): Date {
  const [year, month, day] = isoString.split('T')[0].split('-').map(Number);
  return new Date(year, month - 1, day);
}

function derivePersonType(documentType: string): EPersonType {
  const normalized = (documentType ?? '').toUpperCase();
  if (['CPF', 'PF', 'INDIVIDUAL'].includes(normalized)) {
    return EPersonType.INDIVIDUAL;
  }
  return EPersonType.COMPANY;
}

function buildPersonalData(response: IGetLatestScoreByDocumentResponse, i18n: i18nFn): IPersonalData {
  const meta = response.personMetadata;
  return {
    cpf: response.score.document ?? '',
    nome: meta?.name ?? i18n(NOT_INFORMED_KEY),
    nomeMae: meta?.motherName ?? i18n(NOT_INFORMED_KEY),
    dataNascimento: meta?.birthDate ? DateUtils.formatDate(parseDateLocal(meta.birthDate)) : i18n(NOT_INFORMED_KEY),
    nacionalidade: meta?.nationality ?? i18n(NOT_INFORMED_KEY),
    obito: meta?.deceased
      ? i18n('FinBankCreditScoreInfo.scoreInfo.values.yes')
      : i18n('FinBankCreditScoreInfo.scoreInfo.values.no'),
    escolaridade: meta?.educationLevel ?? i18n(NOT_INFORMED_KEY),
  };
}

function buildCompanyData(response: IGetLatestScoreByDocumentResponse, i18n: i18nFn): ICompanyData {
  const meta = response.companyMetadata;
  return {
    cnpj: response.score.document ?? '',
    razaoSocial: meta?.legalName ?? i18n(NOT_INFORMED_KEY),
    nomeFantasia: meta?.tradeName ?? i18n(NOT_INFORMED_KEY),
    natureza: meta?.legalNature ?? i18n(NOT_INFORMED_KEY),
    situacaoCnpj: meta?.registrationStatus ?? i18n(NOT_INFORMED_KEY),
    atividadePrincipal: meta?.primaryActivity ?? i18n(NOT_INFORMED_KEY),
    dataAbertura: meta?.incorporationDate ? DateUtils.formatDate(parseDateLocal(meta.incorporationDate)) : i18n(NOT_INFORMED_KEY),
    capitalSocial: meta?.currentCapital != null ? StringUtils.formatBRL(meta.currentCapital) : i18n(NOT_INFORMED_KEY),
    dataCapitalSocial: meta?.currentCapitalDate ? DateUtils.formatDate(parseDateLocal(meta.currentCapitalDate)) : i18n(NOT_INFORMED_KEY),
  };
}

function buildContact(response: IGetLatestScoreByDocumentResponse, i18n: i18nFn): IContactInfo {
  return {
    telefonePrincipal: response.contacts?.primaryPhone ? StringUtils.formatTelefone(response.contacts.primaryPhone) : i18n(NOT_INFORMED_KEY),
    telefoneSecundario: response.contacts?.secondaryPhone ? StringUtils.formatTelefone(response.contacts.secondaryPhone) : i18n(NOT_INFORMED_KEY),
  };
}

function buildAddresses(response: IGetLatestScoreByDocumentResponse): IAddressInfo[] {
  return (response.addresses ?? []).map(address => ({
    rua: address.street ?? '',
    bairro: address.neighborhood ?? '',
    cidade: address.city ?? '',
    complemento: address.addressLine2 ?? '',
    estado: address.state ?? '',
    cep: address.postalCode ?? '',
  }));
}

function buildConsumerStatus(response: IGetLatestScoreByDocumentResponse, i18n: i18nFn): IConsumerStatusInfo {
  const isActive = Boolean(response.score.consumerStatus);
  return {
    title: i18n('FinBankCreditScoreInfo.scoreInfo.consumerStatus.title'),
    subtitle: i18n(
      isActive
        ? 'FinBankCreditScoreInfo.scoreInfo.consumerStatus.subtitleActive'
        : 'FinBankCreditScoreInfo.scoreInfo.consumerStatus.subtitleInactive',
    ),
    status: isActive ? EConsumerStatus.ACTIVE : EConsumerStatus.INACTIVE,
    statusLabel: i18n(
      isActive
        ? 'FinBankCreditScoreInfo.scoreInfo.consumerStatus.active'
        : 'FinBankCreditScoreInfo.scoreInfo.consumerStatus.inactive',
    ),
  };
}
